import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { UsersRound } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { UserStats } from "../components/users/UserStats";
import { UsersTable } from "../components/users/UsersTable";
import { EditUserModal } from "../components/users/EditUserModal";
import { CreateUserModal } from "../components/users/CreateUserModal";
import { PasswordResetModal } from "../components/users/PasswordResetModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  SUPERADMIN: "Super Admin",
  GESTION: "Gestión",
  COMERCIAL: "Comercial",
  TECNICO: "Técnico",
  SOPORTE: "Soporte",
  IT: "IT",
};

const roleColors = {
  SUPERADMIN: "bg-purple-600",
  GESTION: "bg-[#03BEE4]",
  COMERCIAL: "bg-[#42E695]",
  TECNICO: "bg-orange-500",
  SOPORTE: "bg-indigo-500",
  IT: "bg-slate-500",
};

// Default color for custom roles
const getDefaultRoleColor = (roleId) => {
  const colors = ["bg-pink-500", "bg-cyan-500", "bg-amber-500", "bg-lime-500", "bg-rose-500"];
  const hash = roleId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const UsersPage = () => {
  const { user: currentUser, canCreate, canEdit, canDelete } = useAuth();
  const [users, setUsers] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  // Admin password-reset state — only used for auth_provider === "local".
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    email: "",
    roles: [],
    notification_email: true,
    notification_inapp: true,
    notify_on_ticket_close: true,
    notify_on_new_assignment: true,
    is_active: true,
  });
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    roles: ["COMERCIAL"],
    notification_email: true,
    notification_inapp: true,
    notify_on_ticket_close: true,
    notify_on_new_assignment: true,
    is_active: true,
    auth_provider: "microsoft",
    responsable_id: null,
    responsable_nombre: null,
  });
  const [creating, setCreating] = useState(false);
  const [managers, setManagers] = useState([]);

  // Fetch users and roles on mount and whenever modals are opened
  useEffect(() => {
    fetchUsers();
    fetchAllRoles();
    fetchManagers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRoles = async () => {
    try {
      // Fetch both system roles and custom roles
      const [systemRes, customRes] = await Promise.all([
        axios.get(`${API_URL}/api/system-roles`),
        axios.get(`${API_URL}/api/roles`).catch(() => ({ data: [] }))
      ]);
      
      // Combine system roles and custom roles
      const allRoles = [
        ...systemRes.data,
        ...customRes.data
      ];
      
      // Remove duplicates by ID
      const uniqueRoles = allRoles.filter((role, index, self) =>
        index === self.findIndex((r) => r.id === role.id)
      );
      
      setSystemRoles(uniqueRoles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchManagers = async () => {
    try {
      const [gRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/by-role/GESTION`),
        axios.get(`${API_URL}/api/users/by-role/SUPERADMIN`),
      ]);
      const combined = [...gRes.data, ...sRes.data];
      const unique = combined.filter((m, i, self) => i === self.findIndex(u => u.id === m.id));
      setManagers(unique);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  // Refresh roles when create modal opens
  useEffect(() => {
    if (createModalOpen) {
      fetchAllRoles();
    }
  }, [createModalOpen]);

  // Refresh roles when edit modal opens
  useEffect(() => {
    if (editModalOpen) {
      fetchAllRoles();
    }
  }, [editModalOpen]);

  // Get role label (from loaded roles or static fallback)
  const getRoleLabel = (roleId) => {
    const loadedRole = systemRoles.find(r => r.id === roleId);
    if (loadedRole) return loadedRole.name || roleId;
    return roleLabels[roleId] || roleId;
  };

  // Get role color (from static or generate default)
  const getRoleColor = (roleId) => {
    return roleColors[roleId] || getDefaultRoleColor(roleId);
  };

  // Get available roles from system roles
  const availableRoles = systemRoles.length > 0 
    ? systemRoles.map(r => r.id) 
    : ["SUPERADMIN", "GESTION", "COMERCIAL", "TECNICO", "SOPORTE"];

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, { is_active: !isActive });
      toast.success(`Usuario ${!isActive ? "activado" : "desactivado"}`);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Error al actualizar usuario");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    // Open confirmation dialog instead of window.confirm
    setUserToDelete({ id: userId, name: userName });
    setDeleteDialogOpen(true);
  };

  const openPasswordModal = (u) => {
    // Only local-auth users have a resettable password in this system.
    // Strict check: we must rely on the backend's normalized auth_provider.
    // Do NOT fall back to "local" when the field is missing — that would
    // incorrectly expose password reset for Microsoft users on legacy data.
    if (u.auth_provider !== "local") {
      toast.error("No se puede cambiar contraseña de un usuario Microsoft");
      return;
    }
    setNewPassword("");
    setConfirmPassword("");
    setPasswordModalUser(u);
  };

  const submitPasswordReset = async () => {
    if (!passwordModalUser) return;
    if (newPassword.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setResettingPassword(true);
    try {
      await axios.put(`${API_URL}/api/users/${passwordModalUser.id}/password`, {
        new_password: newPassword,
      });
      toast.success(`Contraseña actualizada para ${passwordModalUser.name}`);
      setPasswordModalUser(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setResettingPassword(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(userToDelete.id);
    setDeleteDialogOpen(false);
    
    try {
      await axios.delete(`${API_URL}/api/users/${userToDelete.id}`);
      toast.success("Usuario eliminado correctamente");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      const errorMsg = error.response?.data?.detail || "Error al eliminar usuario";
      toast.error(errorMsg);
    } finally {
      setDeleting(null);
      setUserToDelete(null);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!newUser.email.trim()) {
      toast.error("El email es obligatorio");
      return;
    }
    if (newUser.roles.length === 0) {
      toast.error("Selecciona al menos un rol");
      return;
    }
    // Password is optional for Microsoft users
    if (newUser.auth_provider === "local" && !newUser.password.trim()) {
      toast.error("La contraseña es obligatoria para usuarios locales");
      return;
    }

    setCreating(true);
    try {
      const userData = { ...newUser };
      if (userData.auth_provider === "microsoft") {
        delete userData.password;
      }
      await axios.post(`${API_URL}/api/users`, userData);
      toast.success("Usuario creado correctamente");
      setCreateModalOpen(false);
      setNewUser({
        name: "",
        email: "",
        password: "",
        roles: ["COMERCIAL"],
        notification_email: true,
        notification_inapp: true,
        notify_on_ticket_close: true,
        notify_on_new_assignment: true,
        is_active: true,
        auth_provider: "microsoft",
        responsable_id: null,
        responsable_nombre: null,
      });
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of error objects)
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || "Error de validación").join(", ");
        toast.error(messages);
      } else if (typeof errorDetail === "string") {
        toast.error(errorDetail);
      } else {
        toast.error("Error al crear usuario");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleNewUserRoleToggle = (role) => {
    const currentRoles = newUser.roles || [];
    if (currentRoles.includes(role)) {
      if (currentRoles.length > 1) {
        setNewUser({ ...newUser, roles: currentRoles.filter(r => r !== role) });
      } else {
        toast.error("El usuario debe tener al menos un rol");
      }
    } else {
      setNewUser({ ...newUser, roles: [...currentRoles, role] });
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditData({
      name: user.name || "",
      email: user.email || "",
      roles: user.roles || [],
      notification_email: user.notification_email !== false,
      notification_inapp: user.notification_inapp !== false,
      notify_on_ticket_close: user.notify_on_ticket_close !== false,
      notify_on_new_assignment: user.notify_on_new_assignment !== false,
      is_active: user.is_active !== false,
      responsable_id: user.responsable_id || null,
      responsable_nombre: user.responsable_nombre || null,
    });
    setEditModalOpen(true);
  };

  const handleRoleToggle = (role) => {
    const currentRoles = editData.roles || [];
    if (currentRoles.includes(role)) {
      if (currentRoles.length > 1) {
        setEditData({ ...editData, roles: currentRoles.filter(r => r !== role) });
      } else {
        toast.error("El usuario debe tener al menos un rol");
      }
    } else {
      setEditData({ ...editData, roles: [...currentRoles, role] });
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    if (!editData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    if (editData.roles.length === 0) {
      toast.error("El usuario debe tener al menos un rol");
      return;
    }

    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, editData);
      toast.success("Usuario actualizado correctamente");
      setEditModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Error al actualizar usuario");
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "US";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <UsersRound className="h-8 w-8 text-[#03BEE4]" />
            Gestión de Usuarios
          </h1>
          <p className="text-slate-400 mt-1">
            Administra los usuarios del sistema
          </p>
        </div>
        {canCreate("users") && (
          <Button 
            onClick={() => setCreateModalOpen(true)}
            data-testid="create-user-btn"
            className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399] font-semibold"
          >
            <UsersRound className="h-4 w-4 mr-2" />
            Nuevo Usuario
          </Button>
        )}
      </div>

      <UserStats users={users} />

      <UsersTable
        users={users}
        getInitials={getInitials}
        getRoleColor={getRoleColor}
        getRoleLabel={getRoleLabel}
        formatDate={formatDate}
        canEdit={canEdit}
        canDelete={canDelete}
        currentUser={currentUser}
        deleting={deleting}
        openEditModal={openEditModal}
        openPasswordModal={openPasswordModal}
        handleDeleteUser={handleDeleteUser}
        handleToggleActive={handleToggleActive}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        userToDelete={userToDelete}
        confirmDeleteUser={confirmDeleteUser}
      />

      <EditUserModal
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        editData={editData}
        setEditData={setEditData}
        availableRoles={availableRoles}
        handleRoleToggle={handleRoleToggle}
        getRoleColor={getRoleColor}
        getRoleLabel={getRoleLabel}
        managers={managers}
        handleSaveUser={handleSaveUser}
      />

      <CreateUserModal
        createModalOpen={createModalOpen}
        setCreateModalOpen={setCreateModalOpen}
        newUser={newUser}
        setNewUser={setNewUser}
        availableRoles={availableRoles}
        handleNewUserRoleToggle={handleNewUserRoleToggle}
        getRoleColor={getRoleColor}
        getRoleLabel={getRoleLabel}
        managers={managers}
        handleCreateUser={handleCreateUser}
        creating={creating}
      />

      <PasswordResetModal
        passwordModalUser={passwordModalUser}
        setPasswordModalUser={setPasswordModalUser}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        submitPasswordReset={submitPasswordReset}
        resettingPassword={resettingPassword}
      />
    </div>
  );
};

export default UsersPage;
